import React, { useState, useRef, useEffect, useCallback } from "react";
import CustomInputChat from "../../../../components/TextField/TextFeildChat";
import { useFormik } from "formik";
import * as Yup from "yup";
import PushPinIcon from "@mui/icons-material/PushPin";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import "emoji-picker-element";
import { Dropdown, SplitButton } from "react-bootstrap";
import { useSocket } from "../../../../context/SocketContext";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchScheduledChatMessages,
    getOnlineUsersTillNow,
    getRoomSize,
    newMessage,
    roomEvent,
    updatePinnedMessage,
} from "../../../../store/slices/chatSlice";
import { toUnicode } from "../../../../helpers/utils";
import ScheduleChatMessageModal from "../../../../components/models/ScheduleChatMessageModal";
import {
    buildPinnedMessageFromSend,
    normalizePinnedMessage,
} from "./pinnedMessageUtils";

const SendMessageInput = () => {
    const [isChecked, setIsChecked] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleInitialBody, setScheduleInitialBody] = useState("");
    const pickerRef = useRef(null);
    const { user } = useSelector((state) => state.auth);
    const roomId = user?.attributes?.stationChatId;
    const { socket } = useSocket();
    const dispatch = useDispatch();
    const validationSchema = Yup.object({
        message: Yup.string().required("Message can't be empty."),
    });

    const formik = useFormik({
        initialValues: {
            message: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            socket.emit(
                "chat.messages.send",
                {
                    roomId,
                    message: {
                        text: toUnicode(values.message),
                        type: isChecked ? "sticky" : undefined,
                        media: "",
                    },
                },
                (error, response) => {
                    resetForm();
                }
            );
        },
    });

    const hideScheduleModal = useCallback(() => {
        setShowScheduleModal(false);
    }, []);

    const handleOpenScheduleModal = () => {
        setScheduleInitialBody(formik.values.message || "");
        setShowScheduleModal(true);
    };

    useEffect(() => {
        if (socket) {
            // Join the room
            socket.emit("chat.rooms.join", { id: roomId });
            const handleIncomingMessage = (message) => {
                dispatch(newMessage(message));
                const data = message?.data;
                if (data?.attributes?.wasPinnedOnSend) {
                    dispatch(updatePinnedMessage(buildPinnedMessageFromSend(data)));
                }
            };

            socket.on(`messages.send.${roomId}`, handleIncomingMessage);

            return () => {
                socket.off(`messages.send.${roomId}`, handleIncomingMessage);
            };
        }
    }, [dispatch, socket, roomId]);

    useEffect(() => {
        const handleEmojiClick = (e) => {
            formik.setFieldValue("message", formik.values.message + e.detail.unicode);
        };

        if (pickerRef.current) {
            pickerRef.current.addEventListener("emoji-click", handleEmojiClick);
        }

        return () => {
            if (pickerRef.current) {
                pickerRef.current.removeEventListener("emoji-click", handleEmojiClick);
            }
        };
    }, [formik.values.message, formik]);


    useEffect(() => {
        if (!socket || !roomId) return;

        socket.emit("chat.rooms.size", { id: roomId }, (error, response) => {
            if (!error && response?.data) {
                dispatch(getRoomSize(response.data));
            } else {
                console.error("Error getting room size:", error);
            }
        });

        dispatch(getOnlineUsersTillNow({ page: 1, limit: 99 }));
    }, []);


    useEffect(() => {
        if (!roomId || !socket) return;


        const joinEvent = `chat.rooms.join.${roomId}`;
        const messageUpdated = `messages.edit.${roomId}`;
        const messagePinned = `messages.pin.${roomId}`;
        const messageUnpinned = `messages.unpin.${roomId}`;
        const leaveEvent = `chat.rooms.leave.${roomId}`;
        const roomSize = `chat.rooms.size`;
        const pollStartedEvent = `broadcast.pollStarted.${roomId}`;
        const pollEndedEvent = `broadcast.pollEnded.${roomId}`;
        const pollArchivedEvent = `broadcast.pollArchived.${roomId}`;

        socket.on(pollStartedEvent, (response) => {
            dispatch(roomEvent({ eventIs: "pollStarted", payload: response.data }));
        });

        socket.on(pollEndedEvent, (response) => {
            dispatch(roomEvent({ eventIs: "pollEnded", payload: response.data }));
        });

        socket.on(pollArchivedEvent, (response) => {
            dispatch(roomEvent({ eventIs: "pollArchived", payload: response.data }));
        });
        socket.on(roomSize, (response) => {
            dispatch(getRoomSize(response.size));

        });

        socket.on(joinEvent, (response) => {
            console.log('join event : ', response?.data); // checking data on prod
            dispatch(roomEvent({ eventIs: "join", payload: response.data }));
        });

        socket.on(leaveEvent, (response) => {
            dispatch(roomEvent({ eventIs: "leave", payload: response?.data }));
        });

        socket.on(messageUpdated, (response) => {
        });

        socket.on(messagePinned, (response) => {
            const pinPayload = response?.data ?? response;
            const normalized = normalizePinnedMessage(pinPayload);
            if (normalized) {
                dispatch(updatePinnedMessage(normalized));
            }
        });

        socket.on(messageUnpinned, () => {
            dispatch(updatePinnedMessage(null));
        });

        // Clean up
        return () => {
            socket.off(joinEvent);
            socket.off(leaveEvent);
            socket.off(messagePinned);
            socket.off(messageUnpinned);
            socket.off(messageUpdated);
            socket.off(roomSize);
            socket.off(pollStartedEvent);
            socket.off(pollEndedEvent);
            socket.off(pollArchivedEvent);
        };
    }, [socket, roomId, dispatch]);




    return (
        <div className="messageInputBox d-flex" style={{ fontSize: "14px" }}>
            <form className="innerRow message-input-row d-flex" onSubmit={formik.handleSubmit}>
                <div className="emojiDiv message-input-emoji d-flex">
                    <button
                        type="button"
                        className="btn btn-light"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                    >
                        😀
                    </button>

                    {showEmojiPicker && (
                        <div
                            className="position-absolute"
                            style={{
                                bottom: "18px",
                                left: "-18px",
                                zIndex: 99,
                                transform: "scale(0.8)",
                            }}
                        >
                            <emoji-picker ref={pickerRef} style={{ width: "340px" }}></emoji-picker>
                        </div>
                    )}
                </div>

                <div className="message-input-field d-flex">
                    <CustomInputChat
                        id="message"
                        name="message"
                        placeholder={"Type your message..."}
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.message && formik.errors.message}
                        required
                        isTextArea={true}
                    />
                </div>

                <div className="message-input-actions d-flex">
                    <div className="pinnedBox">
                        <input
                            type="checkbox"
                            className="pinned"
                            checked={isChecked}
                            onChange={() => setIsChecked(!isChecked)}
                        />
                        <div className="svgDiv">
                            <PushPinIcon
                                style={{ color: isChecked ? "#007bff" : "#ccc" }}
                            />
                        </div>
                    </div>

                    <div className="button-div send-split-wrap">
                        <SplitButton
                            variant="primary"
                            id="moderator-send-split"
                            type="submit"
                            title={formik.isSubmitting ? "Sending..." : "Send"}
                            align="end"
                            drop="up"
                            renderMenuOnMount
                            toggleLabel="More send options"
                        >
                            <Dropdown.Item
                                eventKey="schedule"
                                className="d-flex align-items-center gap-2 py-0"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleOpenScheduleModal();
                                }}
                            >
                                <span className="position-relative d-inline-flex" style={{ width: 32, height: 24 }}>
                                    <SendIcon sx={{ fontSize: 24, color: "#0d6efd" }} />
                                    <AccessTimeIcon
                                        sx={{
                                            fontSize: 17,
                                            color: "#0d6efd",
                                            position: "absolute",
                                            right: -5,
                                            bottom: -3,
                                        }}
                                    />
                                </span>
                                <span>Schedule send</span>
                            </Dropdown.Item>
                        </SplitButton>
                    </div>
                </div>
            </form>

            <ScheduleChatMessageModal
                show={showScheduleModal}
                onClose={hideScheduleModal}
                initialBody={scheduleInitialBody}
                initialPin={isChecked}
                onScheduleSuccess={() => {
                    formik.resetForm();
                    dispatch(fetchScheduledChatMessages({ active: true }));
                }}
            />
        </div>

    );
};

export default SendMessageInput;
